/* eslint-disable no-console */

import { UIComponent } from './UIComponent.js';

/**
 * Tree Editor Component
 * Handles interactive tree editing interface
 */
export class TreeEditor extends UIComponent {
  constructor(element, treeBuilder, options = {}) {
    super(element, options);
    this.treeBuilder = treeBuilder;
    this.selectedNode = null;
    this.isDragging = false;
    this.dragNode = null;
  }

  getDefaultOptions() {
    return {
      ...super.getDefaultOptions(),
      className: 'tree-editor',
      editable: true,
      selectable: true,
      draggable: true,
      showControls: false
    };
  }

  init() {
    super.init();
    this.setupToolbar();
    this.setupTreeContainer();
    this.renderTree();
    return this;
  }

  /**
   * Setup toolbar controls
   */
  setupToolbar() {
    if (!this.options.showControls) return;

    const toolbar = document.createElement('div');
    toolbar.className = 'tree-toolbar';
    toolbar.innerHTML = `
      <div class="toolbar-group">
        <button id="add-node" class="btn btn-icon" title="Add Node">
          <span class="material-icons">add</span>
          Add Node
        </button>
        <button id="delete-node" class="btn btn-icon" title="Delete Node">
          <span class="material-icons">delete</span>
          Delete
        </button>
        <button id="edit-node" class="btn btn-icon" title="Edit Node">
          <span class="material-icons">edit</span>
          Edit
        </button>
      </div>
      <div class="toolbar-group">
        <button id="expand-all" class="btn btn-icon" title="Expand All">
          <span class="material-icons">expand_more</span>
          Expand All
        </button>
        <button id="collapse-all" class="btn btn-icon" title="Collapse All">
          <span class="material-icons">expand_less</span>
          Collapse All
        </button>
        <button id="reset-view" class="btn btn-icon" title="Reset View">
          <span class="material-icons">refresh</span>
          Reset View
        </button>
      </div>
    `;

    this.element.insertBefore(toolbar, this.element.firstChild);

    // Setup event listeners
    toolbar.querySelector('#add-node').addEventListener('click', (e) => {
      e.preventDefault();
      this.handleAddNode();
    });

    toolbar.querySelector('#delete-node').addEventListener('click', (e) => {
      e.preventDefault();
      this.handleDeleteNode();
    });

    toolbar.querySelector('#edit-node').addEventListener('click', (e) => {
      e.preventDefault();
      this.handleEditNode();
    });

    toolbar.querySelector('#expand-all').addEventListener('click', (e) => {
      e.preventDefault();
      this.handleExpandAll();
    });

    toolbar.querySelector('#collapse-all').addEventListener('click', (e) => {
      e.preventDefault();
      this.handleCollapseAll();
    });

    toolbar.querySelector('#reset-view').addEventListener('click', (e) => {
      e.preventDefault();
      this.handleResetView();
    });
  }

  /**
   * Setup tree container
   */
  setupTreeContainer() {
    this.treeContainer = this.element;
    this.treeContainer.classList.add('tree-container');
  }

  /**
   * Render tree structure
   */
  renderTree() {
    if (!this.treeBuilder.tree) return;

    this.treeContainer.innerHTML = '';
    const treeElement = this.renderNode(this.treeBuilder.tree, 0);
    this.treeContainer.appendChild(treeElement);
  }

  /**
   * Render individual node
   */
  renderNode(node, depth) {
    const nodeElement = document.createElement('div');
    nodeElement.className = 'tree-node';
    nodeElement.dataset.nodeId = node.id;
    nodeElement.style.paddingLeft = `${depth * 24}px`;

    // Node content
    const contentElement = document.createElement('div');
    contentElement.className = 'node-content';

    const nodeType = node.type || 'area';
    const nodeIcon = this.getNodeIcon(nodeType);

    contentElement.innerHTML = `
    <div class="node-header group flex items-center gap-3 rounded-xl border border-transparent bg-white/50 p-2 px-3 transition-all hover:bg-white hover:shadow-sm hover:border-border/50 cursor-pointer">
      <span class="node-icon flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-lg text-primary shadow-sm">${nodeIcon}</span>
      <span class="node-name flex-1 text-sm font-medium text-slate-700 outline-none focus:text-primary" contenteditable="${this.options.editable ? 'true' : 'false'}">${node.name}</span>
      <div class="node-actions flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button class="node-delete-btn inline-flex h-7 w-7 items-center justify-center rounded-lg border transition" style="border-color: rgba(200, 117, 102, 0.3); background-color: rgba(200, 117, 102, 0.1); color: #C87566;" title="Löschen" type="button" onmouseover="this.style.backgroundColor='rgba(200, 117, 102, 0.2)'; this.style.borderColor='rgba(200, 117, 102, 0.4)'" onmouseout="this.style.backgroundColor='rgba(200, 117, 102, 0.1)'; this.style.borderColor='rgba(200, 117, 102, 0.3)'">
          <span class="material-icons text-[16px]">delete</span>
        </button>
        ${this.options.draggable ? '<span class="drag-handle cursor-grab p-1 text-slate-400 hover:text-slate-600 transition-colors"><span class="material-icons text-[18px]">drag_indicator</span></span>' : ''}
      </div>
    </div>
  `;

    nodeElement.appendChild(contentElement);

    // Add children container
    if (node.children && node.children.length > 0) {
      const childrenElement = document.createElement('div');
      childrenElement.className = 'node-children';

      node.children.forEach(child => {
        const childElement = this.renderNode(child, depth + 1);
        childrenElement.appendChild(childElement);
      });

      nodeElement.appendChild(childrenElement);
    }

    // Setup node event listeners
    this.setupNodeEventListeners(nodeElement, node);

    return nodeElement;
  }

  /**
   * Setup event listeners for node
   */
  setupNodeEventListeners(nodeElement, node) {
    // Selection
    if (this.options.selectable) {
      const contentElement = nodeElement.querySelector('.node-content');
      contentElement.addEventListener('click', () => {
        this.selectNode(node, nodeElement);
      });
    }

    // Editing
    if (this.options.editable) {
      const nameElement = nodeElement.querySelector('.node-name');
      nameElement.addEventListener('blur', () => {
        this.handleNodeEdit(node, nameElement.textContent);
      });

      nameElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          nameElement.blur();
        }
      });
    }

    // Delete button
    const deleteBtn = nodeElement.querySelector('.node-delete-btn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (confirm(`"${node.name}" wirklich löschen?`)) {
          this.treeBuilder.removeNode(node.id);
          this.renderTree();
          this.emit('nodeDeleted', node);
        }
      });
    }

    // Drag and drop
    if (this.options.draggable) {
      const dragHandle = nodeElement.querySelector('.drag-handle');
      if (dragHandle) {
        this.setupDragAndDrop(nodeElement, node, dragHandle);
      }
    }

    // Toggle children visibility
    if (node.children && node.children.length > 0) {
      const headerElement = nodeElement.querySelector('.node-header');
      const toggleElement = document.createElement('span');
      toggleElement.className = 'node-toggle';
      toggleElement.textContent = '▼';
      headerElement.insertBefore(toggleElement, headerElement.firstChild);

      toggleElement.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleNode(nodeElement, toggleElement);
      });
    }
  }

  /**
   * Get icon for node type
   */
  getNodeIcon(type) {
    const icons = {
      business: '🏢',
      area: '📍',
      room: '🚪',
      equipment: '🔧',
      storage: '📦',
      office: '👔',
      kitchen: '🍳',
      retail: '🛍️',
      production: '🏭'
    };
    return icons[type] || '📍';
  }

  /**
   * Select node
   */
  selectNode(node, element) {
    // Remove previous selection
    this.element.querySelectorAll('.node-content.selected').forEach(el => {
      el.classList.remove('selected');
    });

    // Add selection to current node
    element.querySelector('.node-content').classList.add('selected');
    this.selectedNode = { node, element };
    if (this.treeBuilder) {
      this.treeBuilder.currentNode = node;
    }
    this.emit('nodeSelected', node);
  }

  /**
   * Handle node edit
   */
  handleNodeEdit(node, newName) {
    const trimmedName = newName.trim();
    if (trimmedName && trimmedName !== node.name) {
      node.name = trimmedName;
      this.emit('nodeChanged', { node, property: 'name', value: trimmedName });
    } else {
      // Revert if empty
      this.renderTree();
    }
  }

  /**
   * Toggle node expansion
   */
  toggleNode(nodeElement, toggleElement) {
    const childrenElement = nodeElement.querySelector('.node-children');
    if (childrenElement) {
      const isExpanded = !childrenElement.style.display || childrenElement.style.display === 'block';
      childrenElement.style.display = isExpanded ? 'none' : 'block';
      toggleElement.textContent = isExpanded ? '▶' : '▼';

      const node = this.treeBuilder.findNode(this.treeBuilder.tree, nodeElement.dataset.nodeId);
      if (node) {
        node.expanded = !isExpanded;
        this.emit('nodeToggled', { node, expanded: !isExpanded });
      }
    }
  }

  /**
   * Setup drag and drop
   */
  setupDragAndDrop(element, node, handle) {
    handle.style.cursor = 'grab';
    handle.setAttribute('draggable', 'true');

    handle.addEventListener('dragstart', (e) => {
      this.isDragging = true;
      this.dragNode = { node, element };
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', element.innerHTML);
      element.classList.add('dragging');
      handle.style.cursor = 'grabbing';
    });

    handle.addEventListener('dragend', () => {
      this.isDragging = false;
      element.classList.remove('dragging');
      this.dragNode = null;
      handle.style.cursor = 'grab';
    });

    element.addEventListener('dragover', (e) => {
      if (this.isDragging && this.dragNode.node !== node) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        element.classList.add('drag-over');
      }
    });

    element.addEventListener('dragleave', () => {
      element.classList.remove('drag-over');
    });

    element.addEventListener('drop', (e) => {
      e.preventDefault();
      element.classList.remove('drag-over');

      if (this.dragNode && this.dragNode.node !== node) {
        this.handleNodeMove(this.dragNode.node, node);
      }
    });
  }

  /**
   * Handle node move
   */
  handleNodeMove(sourceNode, targetNode) {
    // Remove from current parent
    this.treeBuilder.removeNode(sourceNode.id);

    // Add to new parent
    this.treeBuilder.addNode(targetNode.id, {
      name: sourceNode.name,
      type: sourceNode.type,
      metadata: sourceNode.metadata
    });

    this.renderTree();
    this.emit('nodeMoved', { source: sourceNode, target: targetNode });
  }

  /**
   * Toolbar handlers
   */
  handleAddNode() {
    if (!this.selectedNode) {
      console.log('Please select a parent node first');
      return;
    }

    const nodeName = prompt('Enter node name:');
    if (nodeName && nodeName.trim()) {
      const newNode = this.treeBuilder.addNode(this.selectedNode.node.id, {
        name: nodeName.trim(),
        type: 'area'
      });

      this.renderTree();
      this.emit('nodeAdded', newNode);
    }
  }

  handleDeleteNode() {
    if (!this.selectedNode) {
      console.log('Please select a node to delete');
      return;
    }

    if (confirm(`Delete "${this.selectedNode.node.name}" and all its children?`)) {
      this.treeBuilder.removeNode(this.selectedNode.node.id);
      this.renderTree();
      this.emit('nodeDeleted', this.selectedNode.node);
      this.selectedNode = null;
    }
  }

  handleEditNode() {
    if (!this.selectedNode) {
      console.log('Please select a node to edit');
      return;
    }

    const nameElement = this.selectedNode.element.querySelector('.node-name');
    nameElement.focus();

    // Select all text
    const range = document.createRange();
    range.selectNodeContents(nameElement);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  handleExpandAll() {
    this.element.querySelectorAll('.node-children').forEach(childrenElement => {
      childrenElement.style.display = 'block';
    });

    this.element.querySelectorAll('.node-toggle').forEach(toggleElement => {
      toggleElement.textContent = '▼';
    });

    this.emit('treeExpanded');
  }

  handleCollapseAll() {
    this.element.querySelectorAll('.node-children').forEach(childrenElement => {
      childrenElement.style.display = 'none';
    });

    this.element.querySelectorAll('.node-toggle').forEach(toggleElement => {
      toggleElement.textContent = '▶';
    });

    this.emit('treeCollapsed');
  }

  handleResetView() {
    this.selectedNode = null;
    this.renderTree();
    this.handleCollapseAll();
    this.emit('viewReset');
  }

  /**
   * Update tree data
   */
  updateTree(treeData) {
    this.treeBuilder.import(treeData);
    this.renderTree();
  }

  /**
   * Get selected node
   */
  getSelectedNode() {
    return this.selectedNode?.node || null;
  }
}
